import React, { useState, useEffect } from "react";
import { Box, Container, Card, Typography, Grid, Paper, Tabs, Tab, Button, Alert, CircularProgress } from "@mui/material";
import { withStyles } from "@mui/styles";
import { styleSheet } from "./style.js";
import ConversationHistory from "./components/ConversationHistory";
import SystemPrompts from "./components/SystemPrompts";
import { Login as LoginIcon } from "@mui/icons-material";

function AIDiagnostics(props) {
  const { classes } = props;
  const [activeTab, setActiveTab] = useState(0);
  
  // Claude server authentication state
  const [claudeToken, setClaudeToken] = useState(localStorage.getItem('claudeToken'));
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Refresh trigger for tabs when authentication changes
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Login to Claude server
  const handleClaudeLogin = async () => {
    try {
      setLoginLoading(true);
      setLoginError('');
      
      const response = await fetch(`${process.env.REACT_APP_AI_DIAGNOSTIC_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: 'peter',
          password: 'password'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store the token
      localStorage.setItem('claudeToken', data.token);
      setClaudeToken(data.token);
      
      // Trigger refresh of all tabs to load real data
      setRefreshTrigger(prev => prev + 1);
      
      console.log('✅ Successfully logged into Claude server');
    } catch (err) {
      console.error('❌ Claude server login error:', err);
      setLoginError(err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const renderTabContent = () => {
    const tabProps = {
      refreshTrigger,
      claudeToken,
    };
    
    switch (activeTab) {
      case 0:
        return <ConversationHistory {...tabProps} />;
      case 1:
        return <SystemPrompts {...tabProps} />;
      default:
        return <ConversationHistory {...tabProps} />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              AI Agent Diagnostics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor and analyze AI agent performance, tool usage, and system metrics.
            </Typography>
          </Paper>
        </Grid>

        {/* Claude Server Authentication */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                <LoginIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Claude Server Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Authenticate with the Claude server to access AI diagnostic tools.
              </Typography>
              
              {claudeToken ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Authenticated with Claude server
                  </Alert>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      localStorage.removeItem('claudeToken');
                      setClaudeToken(null);
                      // Trigger refresh to clear data
                      setRefreshTrigger(prev => prev + 1);
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Box>
                  {loginError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {loginError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClaudeLogin}
                    disabled={loginLoading}
                    startIcon={loginLoading ? <CircularProgress size={20} /> : <LoginIcon />}
                  >
                    {loginLoading ? 'Logging in...' : 'Login to Claude Server'}
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Using credentials: peter / password
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Tab Navigation */}
        <Grid item xs={12}>
          <Card className={classes.tabCard}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="AI diagnostic tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Conversation History" />
                <Tab label="System Prompts" />
              </Tabs>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {renderTabContent()}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default withStyles(styleSheet)(AIDiagnostics);
